import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Notification,
  NotificationAPIResponse,
  NotificationEventCategory,
} from "../../components/NotificationDrawer/util/types";
import { NOTIFICATION_EVENT_CATEGORY_DISPLAY } from "../../components/NotificationDrawer/util/constants";

interface NotificationTabData {
  unread_events_count: number;
  displayText: string;
  total_events_count: number;
}

export type NotificationTab = {
  [key in NotificationEventCategory]: NotificationTabData;
};

export interface NotificationState {
  isDrawerOpen: boolean;
  activeTab: NotificationEventCategory;
  tabs: NotificationTab;
  notifications: Notification[];
  nextCursor: string | null;
  hasMore: boolean;
}

const buildEmptyTab = (
  category: NotificationEventCategory
): NotificationTabData => {
  return {
    unread_events_count: 0,
    displayText: NOTIFICATION_EVENT_CATEGORY_DISPLAY[category],
    total_events_count: 0,
  };
};

const initialState: NotificationState = {
  isDrawerOpen: false,
  activeTab: NotificationEventCategory.ACTIVITY,
  tabs: {
    [NotificationEventCategory.ACTIVITY]: buildEmptyTab(
      NotificationEventCategory.ACTIVITY
    ),
    [NotificationEventCategory.SUPPORT]: buildEmptyTab(
      NotificationEventCategory.SUPPORT
    ),
    [NotificationEventCategory.RESPONSE]: buildEmptyTab(
      NotificationEventCategory.RESPONSE
    ),
    [NotificationEventCategory.TECHNICAL_ISSUES]: buildEmptyTab(
      NotificationEventCategory.TECHNICAL_ISSUES
    ),
    [NotificationEventCategory.ORG]: buildEmptyTab(
      NotificationEventCategory.ORG
    ),
  },
  notifications: [],
  nextCursor: null,
  hasMore: true,
};

export const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    toggleDrawer: (state) => {
      state.isDrawerOpen = !state.isDrawerOpen;
    },
    updateActiveTab: (
      state,
      action: PayloadAction<NotificationEventCategory>
    ) => {
      state.activeTab = action.payload;
      state.notifications = [];
    },
    initializeTabData: (
      state,
      action: PayloadAction<NotificationAPIResponse>
    ) => {
      const {
        unread_events_count,
        events = [],
        total_events_count,
        next_cursor,
        has_more,
      } = action.payload;
      const newTabsData: NotificationTab = Object.keys(
        unread_events_count || {}
      )?.reduce((acc, key) => {
        acc[key as keyof NotificationTab] = {
          unread_events_count:
            unread_events_count[key as NotificationEventCategory],
          displayText:
            NOTIFICATION_EVENT_CATEGORY_DISPLAY[
            key as NotificationEventCategory
            ],
          total_events_count:
            total_events_count[key as NotificationEventCategory] || 0,
        };
        return acc;
      }, {} as NotificationTab);
      state.notifications = events;
      state.tabs = newTabsData;
      state.nextCursor = next_cursor;
      state.hasMore = has_more;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      const { event_category_v2, scope } = action.payload;
      if (state.tabs[event_category_v2] && scope !== NotificationEventCategory.ORG) {
        state.tabs[event_category_v2].unread_events_count++;
        state.tabs[event_category_v2].total_events_count++;
      }

      if (
        scope === NotificationEventCategory.ORG &&
        event_category_v2 !== NotificationEventCategory.ORG
      ) {
        state.tabs[NotificationEventCategory.ORG].unread_events_count++;
        state.tabs[NotificationEventCategory.ORG].total_events_count++;
      }
      if (
        (scope === NotificationEventCategory.ORG &&
          state.activeTab === NotificationEventCategory.ORG) || (scope !== NotificationEventCategory.ORG && state.activeTab === event_category_v2)
      ) {
        state.notifications.unshift(action.payload);
      }
    },
    markNotificationAsRead: (
      state,
      action: PayloadAction<{ tab: string; notificationId: string }>
    ) => {
      const { tab, notificationId } = action.payload;
      if (state.tabs[tab as NotificationEventCategory] && notificationId) {
        const newNotifications = state.notifications.map((notification) => {
          if (notification.id === notificationId) {
            return { ...notification, is_read: true };
          }
          return notification;
        });
        if (
          state.tabs[tab as NotificationEventCategory] &&
          state.tabs[tab as NotificationEventCategory].unread_events_count > 0
        ) {
          state.tabs[tab as NotificationEventCategory].unread_events_count--;
        }
        state.notifications = newNotifications;
      }
    },
    markAllNotificationsAsRead: (
      state,
      action: PayloadAction<{ tab: string }>
    ) => {
      const { tab } = action.payload;
      if (state.tabs[tab as NotificationEventCategory]) {
        const newNotifications = state.notifications.map((notification) => {
          return { ...notification, is_read: true };
        });
        state.notifications = newNotifications;
        state.tabs[tab as NotificationEventCategory].unread_events_count = 0;
      }
    },
    loadMoreNotifications: (
      state,
      action: PayloadAction<NotificationAPIResponse>
    ) => {
      const {
        events = [],
        total_events_count,
        unread_events_count,
        next_cursor,
        has_more,
      } = action.payload;
      const newTabsData: NotificationTab = Object.keys(
        unread_events_count || {}
      )?.reduce((acc, key) => {
        acc[key as keyof NotificationTab] = {
          unread_events_count:
            unread_events_count[key as NotificationEventCategory],
          displayText:
            NOTIFICATION_EVENT_CATEGORY_DISPLAY[
            key as NotificationEventCategory
            ],
          total_events_count:
            total_events_count[key as NotificationEventCategory] || 0,
        };
        return acc;
      }, {} as NotificationTab);
      state.notifications.push(...events);
      state.tabs = newTabsData;
      state.nextCursor = next_cursor;
      state.hasMore = has_more;
    },
  },
});

export const {
  toggleDrawer,
  updateActiveTab,
  initializeTabData,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  loadMoreNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
